<template>
  <div class="rules">
    <page-section title="Rules">
      <div class="rules-header" slot="title">
        <el-button data-testid="add-rule" size="small" @click="openCreate">+ Add Rule</el-button>
      </div>

      <el-table :data="rulesets" v-loading="loading" stripe style="width: 100%">
        <el-table-column prop="name" label="Name" />
        <el-table-column prop="infectionSite" label="Infection Site" width="150" />
        <el-table-column prop="infectionSubtype" label="Subtype" />
        <el-table-column prop="useCase" label="Use Case"  width="200">
          <template slot-scope="scope">
              {{ useCaseLabel(scope.row.useCase) }}
          </template>
        </el-table-column>
        <el-table-column prop="description" label="Description" />
        <el-table-column label="Status" width="100">
          <template slot-scope="scope">
            <span :class="scope.row.active ? 'status--active' : 'status--inactive'">
              {{ scope.row.active ? 'Active' : 'Inactive' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="160">
          <template slot-scope="scope">
            <el-button data-testid="edit-rule" size="mini" @click="openEdit(scope.row)">Edit</el-button>
            <el-button data-testid="deactivate-rule" v-if="scope.row.active" size="mini" type="danger" plain
              @click="confirmDeactivationVisible = scope.row.id">Deactivate</el-button>
            <el-button data-testid="activate-rule" v-else size="mini" type="success" plain @click="activateRule(scope.row.id)">Activate</el-button>
          </template>
        </el-table-column>
      </el-table>

    </page-section>

    <!-- Create / Edit dialog -->
    <el-dialog :title="editorMode === 'create' ? 'Create Rule' : 'Edit Rule'" v-model="dialogVisible"
      :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
      <div style="overflow: auto; max-height: 70vh; padding-right: 0.5rem;">
        <ruleset-editor :ruleset="draftRuleset" />
      </div>
      <div slot="footer">
        <el-button data-testid="cancel-rule" @click="cancelDialog" :disabled="saving">Cancel</el-button>
        <el-button data-testid="save-rule" type="primary" @click="saveRule" :loading="saving" :disabled="!canSave">Save</el-button>
      </div>
    </el-dialog>

    <!-- Deactivate Confirmation -->
    <el-dialog title="Confirm" size="tiny" v-model="showDeleteConfirmation" :close-on-click-modal="false"
      :close-on-press-escape="false" :show-close="false">
      <div style="padding-right: 1rem;">
        Are you sure you want to deactivate this rule?
      </div>
      <div slot="footer">
        <el-button data-testid="cancel-deactivate" @click="confirmDeactivationVisible = null" :disabled="saving">Cancel</el-button>
        <el-button data-testid="confirm-deactivate" @click="deactivateRule()" type="primary">Deactivate</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import RulesetEditor from "../../Shared/RulesetEditor.vue";
import PageSection from "../../Shared/PageSection.vue";
import { USE_CASE_OPTIONS, hasIntegerThresholds, isThresholdUseCase } from "../../Shared/ruleset.constants";

const RULE_SAVE_FAILED_MESSAGE = "The rule could not be saved. Check the values and try again.";

export default {
  name: "Rules",
  components: { RulesetEditor, PageSection },
  data() {
    return {
      rulesets: [],
      loading: false,
      saving: false,
      dialogVisible: false,
      confirmDeactivationVisible: null,
      editorMode: "create",
      draftRuleset: null,
    };
  },
  computed: {
    showDeleteConfirmation() {
      return !!this.confirmDeactivationVisible;
    },
    // Outbreak thresholds apply to a whole infection site, so they carry no subtype; their
    // counts must be whole numbers or the API rejects the save.
    canSave() {
      const draft = this.draftRuleset;
      if (!draft) return false;
      const isThreshold = isThresholdUseCase(draft.useCase);
      const subtypeOk = isThreshold || !!draft.infectionSubtype;
      const bodyOk = isThreshold ? hasIntegerThresholds(draft.ruleset) : !!draft.ruleset;
      return !!(draft.name && draft.useCase && draft.infectionSite && subtypeOk && bodyOk);
    }
  },
  methods: {
    useCaseLabel(useCase) {
      const option = USE_CASE_OPTIONS.find((o) => o.value === useCase);
      return option ? option.label : useCase || "";
    },
    async fetchRules() {
      this.loading = true;
      try {
        const resp = await this.$http.post(`${window.CONFIG.infection_api}/rules/get`);
        this.rulesets = await resp.json();
        this.rulesets = this.rulesets.sort((a, b) => a.infectionSite.localeCompare(b.infectionSite));
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editorMode = "create";
      this.draftRuleset = { name: "", infectionSite: "", infectionSubtype: "", description: "", active: true, ruleset: null, useCase: "" };
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.editorMode = "edit";
      this.draftRuleset = JSON.parse(JSON.stringify(row));
      this.dialogVisible = true;
    },
    cancelDialog() {
      this.dialogVisible = false;
      this.draftRuleset = null;
    },
    async saveRule() {
      this.saving = true;
      try {
        await this.$http.post(`${window.CONFIG.infection_api}/rules/create`, JSON.stringify(this.draftRuleset));
        this.dialogVisible = false;
        this.draftRuleset = null;
        await this.fetchRules();
      } catch (error) {
        this.$message.error(RULE_SAVE_FAILED_MESSAGE);
      } finally {
        this.saving = false;
      }
    },
    async deactivateRule() {
      await this.$http.patch(`${window.CONFIG.infection_api}/rules/deactivate/${this.confirmDeactivationVisible}`);
      await this.fetchRules();
      this.confirmDeactivationVisible = null;
    },
    async activateRule(id) {
      await this.$http.patch(`${window.CONFIG.infection_api}/rules/activate/${id}`);
      await this.fetchRules();
    }
  },
  mounted() {
    this.fetchRules();
  }
};
</script>

<style scoped>
.rules-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

}

.rules-title {
  margin: 0;
  font-size: 1.2rem;
}

.status--active {
  color: #3a7a1a;
  font-weight: bold;
}

.status--inactive {
  color: #999;
}
</style>
